import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, DialogActions, DialogContent, DialogTitle, styled } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { ResponsiveDialog, UnsavedDialog, useSelection, useSettings, useUserData, Utils } from 'app/main/components';
import { ViewTable, ViewTableUtils, type ViewFooterCustomInfoProps } from 'app/main/components/page-layouts';

import { calcPricing, getDefaultQuery } from '../components/OrdersLinesTableUtils';
import OrdersLinesAddItemToolbar from '../components/OrdersLinesAddItemToolbar';
import OrdersLinesTableFooterInfo from '../components/OrdersLinesTableFooterInfo';
import QuantitySelectCell from '../components/QuantitySelectCell';

interface OrdersLinesAddItemDialogProps {
	openDialog: boolean;
	closeDialog: VoidFunction;
	orderUid: string;
	compUid: string;
	pricingGroupId: string;
	discountPercentage: number;
	existingRows: any[];
	updateRefresh: VoidFunction;
}

const StyledViewTable = styled(ViewTable)(({ theme }) => ({
	'.MuiDataGrid-cell.OrdersLinesAddTable-quantityCell': {
		padding: 0
	}
}));

export default function OrdersLinesAddItemDialog(props: OrdersLinesAddItemDialogProps) {
	const dispatch = useDispatch();
	const { sysSettings, t } = useSettings();
	const userData = useUserData();

	const onDialogClose = () => document.dispatchEvent(new Event('onOrdersLinesAddItemDialogClose'));

	const defaultQuery = React.useMemo(
		() => getDefaultQuery(props.pricingGroupId, props.compUid),
		[props.compUid, props.pricingGroupId]
	);

	const areItemLinesGrouped = sysSettings.orders_lines_items_grouped === 'true';

	const [hasModelChanged, setHasModelChanged] = React.useState(false);

	const defaultSelectedItems = React.useMemo(
		() => (areItemLinesGrouped ? props.existingRows || [] : []),
		[areItemLinesGrouped, props.existingRows]
	);

	const parseSelectedItems = React.useCallback(
		(newItems: any[]) => {
			setHasModelChanged(true);
			return newItems.map(item => {
				const newQuantity = item.orli_quantity || item.item_ords_multiple || 1;
				return calcPricing({ ...item, orli_quantity: newQuantity }, props.discountPercentage);
			});
		},
		[props.discountPercentage]
	);

	const { selectedItems, setSelectedItems } = useSelection({
		module: 'items',
		isSelecting: true,
		defaultSelectedItems,
		parseSelectedItems
	});

	const rowsParser = (rows: any[]) =>
		rows.map(row => {
			const selectedItem = selectedItems.find(item => item.orli_item_uid === row.itpr_item_uid) || {};
			return calcPricing({ ...row, ...selectedItem }, props.discountPercentage);
		});

	const handleQuantityChange = (item: any, newQuantity?: number) => {
		setHasModelChanged(true);
		setSelectedItems([
			...selectedItems.filter(prevItem => prevItem.orli_item_uid !== item.orli_item_uid),
			calcPricing({ ...item, orli_quantity: newQuantity }, props.discountPercentage)
		]);
	};

	function addLines() {
		dispatch(Utils.alertProgressMsg(t));
		Utils.executeStored({
			action: 'st_orders_lines_add',
			dispatch,
			data: {
				orli_ords_uid: props.orderUid,
				orli_user_created_uid: userData.user_uid,
				lines: selectedItems.map(item => ({
					orli_type: 'item',
					orli_item_uid: item.orli_item_uid,
					orli_quantity: item.orli_quantity
				}))
			},
			successHandler: () => {
				dispatch(Utils.alertSuccessMsg(t));
				props.closeDialog();
				props.updateRefresh();
			}
		});
	}

	const columns: GridColDef[] = [
		{
			field: 'item_erp_id',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_ERP_ID'),
			minWidth: 120,
			renderCell: ViewTableUtils.renderCellWithTooltip
		},
		{
			field: 'item_title',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_ITEM'),
			minWidth: 400,
			flex: 4,
			renderCell: ViewTableUtils.renderCellWithTooltip,
			hideable: false
		},
		{
			field: 'itsr_title',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_ITEM_SERIES'),
			minWidth: 100,
			renderCell: ViewTableUtils.renderCellWithTooltip
		},
		{
			field: 'itms_title',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_ITEM_MEASURES'),
			minWidth: 100,
			renderCell: ViewTableUtils.renderCellWithTooltip
		},
		{
			field: 'orli_quantity',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_QUANTITY'),
			minWidth: 100,
			cellClassName: 'OrdersLinesAddTable-quantityCell',
			renderCell: ({ value, row }) => (
				<QuantitySelectCell
					quantity={value}
					pricingsLots={row.orli_pricings_lots}
					multiples={row.item_ords_multiple}
					minQty={row.item_ords_qty_min}
					discountPercentage={props.discountPercentage}
					handleChange={newQuantity => handleQuantityChange(row, newQuantity)}
				/>
			),
			sortable: false,
			hideable: false
		},
		{
			field: 'orli_base_price',
			type: 'number',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_BASE_PRICE'),
			minWidth: 125,
			renderCell: ViewTableUtils.renderCurrency,
			sortable: false
		},
		{
			field: 'orli_discount',
			type: 'number',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_DISCOUNT'),
			minWidth: 100,
			renderCell: ViewTableUtils.renderPercentage,
			sortable: false
		},
		{
			field: 'orli_unit_price',
			type: 'number',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_UNIT_PRICE'),
			minWidth: 125,
			renderCell: ViewTableUtils.renderCurrency,
			sortable: false,
			hideable: false
		},
		{
			field: 'orli_tot_value_tax_excl',
			type: 'number',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_TOT_TAX_EXCL'),
			minWidth: 125,
			renderCell: ViewTableUtils.renderCurrency,
			sortable: false,
			hideable: false
		}
	];

	const FooterCustomInfo = React.useCallback((footerProps: ViewFooterCustomInfoProps) => {
		const totTaxExcl = Utils.calcObjectsTotal(footerProps.selectedItems || [], 'orli_tot_value_tax_excl');
		return <OrdersLinesTableFooterInfo totTaxExcl={totTaxExcl} />;
	}, []);

	return (
		<ResponsiveDialog
			open={props.openDialog}
			onClose={onDialogClose}
			maxWidth="xl"
			fullWidth
			slotProps={{ paper: { className: 'h-full' } }}
		>
			<UnsavedDialog
				hasModelChanged={hasModelChanged}
				eventName="onOrdersLinesAddItemDialogClose"
				onClose={() => {
					setSelectedItems(defaultSelectedItems);
					props.closeDialog();
				}}
			/>
			<DialogTitle>{t('DIALOG_ORDERS_LINES_DIALOG_ADD_ITEMS_TITLE')}</DialogTitle>
			<DialogContent className="pb-0">
				<StyledViewTable
					className="h-full"
					module="items_pricings"
					idField="item_uid"
					query={{ storedProcedure: 'st_items_pricings_list_active' }}
					columns={columns}
					defaultFilters={[
						{
							filt_field: 'item_blocked',
							filt_clause: 'not_equal',
							filt_value: 1
						},
						{
							filt_field: 'item_itty_id',
							filt_clause: 'in',
							filt_value:
								Utils.getConnectionFromToken() === 'PERCEPTIVE_CONTROL_BEB' ? [2, 3, 6, 7, 9] : []
						}
					]}
					defaultQuery={defaultQuery}
					showExportButton={false}
					defaultNRows={100}
					slots={{
						toolbar: OrdersLinesAddItemToolbar,
						footerCustomInfo: FooterCustomInfo
					}}
					rowsParser={rowsParser}
					selectionProps={{ mode: 'multiple', selectedItems }}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={onDialogClose}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button color="secondary" onClick={addLines} disabled={selectedItems.length === 0}>
					{t('DIALOG_ACTION_ADD')}
				</Button>
			</DialogActions>
		</ResponsiveDialog>
	);
}
